// combatpro/panels/monsters/studio/MonsterStudioWorkspace.js
//
// Combat PRO - MonsterStudioWorkspace (Stage 54 shell; editor/library/persistence/preview).
//
// Instantiates the Monster Studio runtimes and wires them to the presentation:
//   - useMonsterStudioEditor      - owns the working model, tab, dirty, editing.
//   - useMonsterStudioLibrary     - sections, search, filters, collections, loading.
//   - useMonsterStudioPersistence - CRUD, version history, cloud save (boot-loads
//                                   the custom pool into the library).
//   - useMonsterStudioPreview     - read-only preview mode/open state + print.
//   - useMonsterStudioImportExport - JSON file export + import (parse, normalize,
//                                    load as dirty); owns the hidden file input.
// The Header shows the display name + live counts and routes New / sections; the
// Sidebar renders the live library; Source / Tabs / TabPanel render the editor; the
// Footer routes Save / Save As / Duplicate / Favourite / Archive (or Restore) /
// Delete / Preview / Export / Import. The Versions and Preview modals and the toasts
// mount here; the hidden import input is wired to the import/export runtime. The
// preview reads the live editor model directly - no copy. Stage 62 instantiates the
// encounter bridge (useMonsterStudioEncounterBridge) and wires the Footer Test In
// Encounter button to it; the bridge toast mounts alongside the others. Stage 63
// instantiates the Encounter Template runtime (useMonsterStudioTemplates), routes the
// Header Templates button to open it, and mounts the MonsterStudioTemplates modal plus
// its toast. No CombatContext changes. No optional chaining. No em dash.

import React from 'react';
import useMonsterStudioEditor from './useMonsterStudioEditor';
import useMonsterStudioLibrary from './useMonsterStudioLibrary';
import useMonsterStudioPersistence from './useMonsterStudioPersistence';
import useMonsterStudioPreview from './useMonsterStudioPreview';
import useMonsterStudioImportExport from './useMonsterStudioImportExport';
import useMonsterStudioEncounterBridge from './useMonsterStudioEncounterBridge';
import useMonsterStudioTemplates from './useMonsterStudioTemplates';
import MonsterStudioHeader from './MonsterStudioHeader';
import MonsterStudioSidebar from './MonsterStudioSidebar';
import MonsterStudioSource from './MonsterStudioSource';
import MonsterStudioTabs from './MonsterStudioTabs';
import MonsterStudioTabPanel from './MonsterStudioTabPanel';
import MonsterStudioFooter from './MonsterStudioFooter';
import MonsterStudioVersions from './MonsterStudioVersions';
import MonsterStudioPreview from './MonsterStudioPreview';
import MonsterStudioToast from './MonsterStudioToast';
import MonsterStudioTemplates from './MonsterStudioTemplates';

function MonsterStudioWorkspace() {
  const editor = useMonsterStudioEditor();
  const library = useMonsterStudioLibrary({ onLoad: editor.loadModel });
  const persistence = useMonsterStudioPersistence({ editor: editor, library: library });
  const preview = useMonsterStudioPreview();
  const importExport = useMonsterStudioImportExport({ editor: editor, library: library });
  const bridge = useMonsterStudioEncounterBridge({ editor: editor, library: library });
  const templates = useMonsterStudioTemplates();

  var model = editor.model || {};

  return (
    <>
      <MonsterStudioHeader
        displayName={editor.displayName}
        onNewMonster={editor.newMonster}
        onSetSection={library.setSection}
        officialCount={library.officialCount}
        customCount={library.customCount}
        onOpenTemplates={templates.openTemplates}
      />
      <div className="vi-st-layout">
        <MonsterStudioSidebar library={library} />
        <section className="vi-st-main">
          <div className="vi-st-editrow">
            <MonsterStudioSource model={editor.model} dirty={editor.dirty} onVersions={persistence.openVersions} />
            <div className="vi-st-editcol">
              <MonsterStudioTabs activeTab={editor.activeTab} onSelectTab={editor.setActiveTab} />
              <MonsterStudioTabPanel editor={editor} />
            </div>
          </div>
          <MonsterStudioFooter
            busy={persistence.busy}
            archived={!!model.archived}
            onSave={persistence.save}
            onSaveAs={persistence.saveAs}
            onDuplicate={persistence.duplicate}
            onFavourite={persistence.favourite}
            onArchive={persistence.archive}
            onRestore={persistence.restore}
            onDelete={persistence.remove}
            onPreview={preview.open}
            onExport={importExport.exportJson}
            onImport={importExport.triggerImport}
            onTest={bridge.testInEncounter}
          />
        </section>
      </div>
      <MonsterStudioVersions
        open={persistence.versionsOpen}
        model={editor.model}
        onClose={persistence.closeVersions}
        onRestore={persistence.versionRestore}
        onDuplicate={persistence.versionDuplicate}
        onDelete={persistence.versionDelete}
      />
      <MonsterStudioPreview
        open={preview.isOpen}
        mode={preview.mode}
        model={editor.model}
        onClose={preview.close}
        onSetMode={preview.setMode}
        onPrint={function() { preview.print(editor.model); }}
      />
      <MonsterStudioTemplates
        open={templates.open}
        templates={templates.templates}
        onClose={templates.closeTemplates}
        onSaveCurrent={templates.saveCurrent}
        onLoad={templates.load}
        onDuplicate={templates.duplicate}
        onDelete={templates.remove}
      />
      <MonsterStudioToast message={persistence.message} messageId={persistence.messageId} />
      <MonsterStudioToast id="vi-st-toast-import" message={importExport.message} messageId={importExport.messageId} />
      <MonsterStudioToast id="vi-st-toast-bridge" message={bridge.message} messageId={bridge.messageId} />
      <MonsterStudioToast id="vi-st-toast-tpl" message={templates.message} messageId={templates.messageId} />
      <input
        type="file"
        id="vi-st-import-file"
        accept="application/json,.json"
        style={{ display: 'none' }}
        ref={importExport.inputRef}
        onChange={importExport.onInputChange}
      />
    </>
  );
}

export default MonsterStudioWorkspace;
